// ============================================
// FILE: src/hooks/useModuleAccess.js
// ============================================

import { useState, useEffect, useCallback } from 'react';
import api from '../api';

export const useModuleAccess = (tenantId) => {
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasActivePlan, setHasActivePlan] = useState(false);
  const [planName, setPlanName] = useState('No Plan');

  const fetchModules = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // ✅ Fetch from backend - NO CACHING
      const response = await api.get(`/api/modules/available/${tenantId}`);

      if (response.data.success) {
        const data = response.data.data;
        
        // Convert modules array to object for easy lookup
        const modulesObject = {};
        if (data.modules && Array.isArray(data.modules)) {
          data.modules.forEach(m => {
            modulesObject[m.module] = m.accessible;
          });
        }

        setModules(modulesObject);
        setHasActivePlan(data.hasActivePlan || false);
        setPlanName(data.planName || 'No Plan');
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError(err.response?.data?.message || 'Failed to fetch modules');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Check if a specific module is accessible
  const hasModuleAccess = useCallback((moduleName) => {
    return modules[moduleName] === true;
  }, [modules]);

  // Force refresh modules
  const refreshModules = useCallback(() => {
    return fetchModules();
  }, [fetchModules]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  return {
    modules,
    loading,
    error,
    hasActivePlan,
    planName,
    hasModuleAccess,
    refreshModules
  };
};